import { useCallback, useEffect, useMemo, useState } from 'react';
import { getBackendUrl } from '../lib/apiUrls';
import { createFamilyClient } from '../lib/familyClient';

function normalizeFamilyList(list) {
  return {
    ...list,
    id: list?.id || list?.listId || list?._id || '',
    scope: 'family',
  };
}

function normalizeFamilyItem(item) {
  return {
    ...item,
    id: item?.id || item?.itemId || item?._id || '',
  };
}

export default function useFamilyLists(currentUser) {
  const familyClient = useMemo(() => createFamilyClient(getBackendUrl()), []);
  const [family, setFamily] = useState(null);
  const [familyLists, setFamilyLists] = useState([]);
  const [selectedFamilyListId, setSelectedFamilyListId] = useState('');
  const [selectedFamilyListItems, setSelectedFamilyListItems] = useState([]);
  const [familyListsStatus, setFamilyListsStatus] = useState({
    type: 'idle',
    message: '',
  });
  const [isLoadingFamilyLists, setIsLoadingFamilyLists] = useState(false);
  const [hasLoadedFamilyLists, setHasLoadedFamilyLists] = useState(false);

  const selectedFamilyList = useMemo(
    () => familyLists.find((list) => list.id === selectedFamilyListId) || null,
    [familyLists, selectedFamilyListId]
  );

  const refreshFamilyLists = useCallback(async () => {
    if (!currentUser?.userId) {
      setFamily(null);
      setFamilyLists([]);
      setSelectedFamilyListId('');
      setSelectedFamilyListItems([]);
      setFamilyListsStatus({ type: 'idle', message: '' });
      setHasLoadedFamilyLists(true);
      return [];
    }

    setIsLoadingFamilyLists(true);
    setFamilyListsStatus({ type: 'loading', message: 'Loading family shared lists...' });

    try {
      const familyResponse = await familyClient.getFamily(currentUser.userId);
      const resolvedFamily = familyResponse?.family || familyResponse?.data?.family || familyResponse || null;
      setFamily(resolvedFamily?._id || resolvedFamily?.id ? resolvedFamily : null);

      if (!(resolvedFamily?._id || resolvedFamily?.id)) {
        setFamilyLists([]);
        setFamilyListsStatus({ type: 'success', message: 'No family shared lists yet.' });
        return [];
      }

      const listsResponse = await familyClient.getFamilyLists(currentUser.userId);
      const nextLists = (Array.isArray(listsResponse?.lists) ? listsResponse.lists : listsResponse || []).map(
        normalizeFamilyList
      );
      setFamilyLists(nextLists);
      setFamilyListsStatus({
        type: 'success',
        message: nextLists.length ? 'Family shared lists loaded.' : 'No family shared lists yet.',
      });
      return nextLists;
    } catch (error) {
      setFamilyListsStatus({
        type: 'error',
        message: error?.error || error?.message || 'Could not load family shared lists.',
      });
      return [];
    } finally {
      setHasLoadedFamilyLists(true);
      setIsLoadingFamilyLists(false);
    }
  }, [currentUser?.userId, familyClient]);

  const loadFamilyListItems = useCallback(
    async (listId, status = 'all') => {
      if (!currentUser?.userId || !listId) {
        setSelectedFamilyListItems([]);
        return { list: null, items: [] };
      }

      setIsLoadingFamilyLists(true);

      try {
        const response = await familyClient.getFamilyListItems(currentUser.userId, listId, { status });
        const nextList = response?.list ? normalizeFamilyList(response.list) : null;
        const nextItems = (Array.isArray(response?.items) ? response.items : []).map(normalizeFamilyItem);

        if (nextList) {
          setFamilyLists((current) => {
            const exists = current.some((list) => list.id === nextList.id);
            if (!exists) {
              return [...current, nextList];
            }

            return current.map((list) => (list.id === nextList.id ? { ...list, ...nextList } : list));
          });
        }

        setSelectedFamilyListId(listId);
        setSelectedFamilyListItems(nextItems);

        return { list: nextList, items: nextItems };
      } catch (error) {
        setFamilyListsStatus({
          type: 'error',
          message: error?.error || error?.message || 'Could not load family list items.',
        });
        setSelectedFamilyListItems([]);
        return { list: null, items: [] };
      } finally {
        setIsLoadingFamilyLists(false);
      }
    },
    [currentUser?.userId, familyClient]
  );

  useEffect(() => {
    void refreshFamilyLists();
  }, [refreshFamilyLists]);

  const openFamilyList = useCallback(
    async (listId, status = 'all') => {
      await loadFamilyListItems(listId, status);
    },
    [loadFamilyListItems]
  );

  const createFamilyList = useCallback(
    async (list) => {
      if (!currentUser?.userId) {
        return null;
      }

      setIsLoadingFamilyLists(true);
      try {
        const response = await familyClient.createFamilyList(currentUser.userId, list);
        const createdList = normalizeFamilyList(response?.list || response?.data?.list || response || {});
        await refreshFamilyLists();
        return createdList.id ? createdList : null;
      } catch (error) {
        setFamilyListsStatus({
          type: 'error',
          message: error?.error || error?.message || 'Could not create family list.',
        });
        return null;
      } finally {
        setIsLoadingFamilyLists(false);
      }
    },
    [currentUser?.userId, familyClient, refreshFamilyLists]
  );

  const updateFamilyList = useCallback(
    async (listId, updates) => {
      if (!currentUser?.userId || !listId) {
        return null;
      }

      setIsLoadingFamilyLists(true);
      try {
        const response = await familyClient.updateFamilyList(currentUser.userId, listId, updates);
        const updatedList = normalizeFamilyList(response?.list || response?.data?.list || response || {});
        await refreshFamilyLists();
        await loadFamilyListItems(updatedList.id || listId);
        return updatedList.id ? updatedList : null;
      } catch (error) {
        setFamilyListsStatus({
          type: 'error',
          message: error?.error || error?.message || 'Could not update family list.',
        });
        return null;
      } finally {
        setIsLoadingFamilyLists(false);
      }
    },
    [currentUser?.userId, familyClient, loadFamilyListItems, refreshFamilyLists]
  );

  const createFamilyListItem = useCallback(
    async (listId, item) => {
      if (!currentUser?.userId || !listId) {
        return null;
      }

      setIsLoadingFamilyLists(true);
      try {
        const response = await familyClient.createFamilyListItems(currentUser.userId, listId, {
          items: [item],
        });
        const createdItem = normalizeFamilyItem(response?.item || response?.data?.item || response || {});
        await loadFamilyListItems(listId);
        await refreshFamilyLists();
        return createdItem.id ? createdItem : null;
      } catch (error) {
        setFamilyListsStatus({
          type: 'error',
          message: error?.error || error?.message || 'Could not create family list item.',
        });
        return null;
      } finally {
        setIsLoadingFamilyLists(false);
      }
    },
    [currentUser?.userId, familyClient, loadFamilyListItems, refreshFamilyLists]
  );

  const updateFamilyListItem = useCallback(
    async (listId, itemId, updates) => {
      if (!currentUser?.userId || !listId || !itemId) {
        return null;
      }

      setIsLoadingFamilyLists(true);
      try {
        const response = await familyClient.updateFamilyListItem(
          currentUser.userId,
          listId,
          itemId,
          updates
        );
        const updatedItem = normalizeFamilyItem(response?.item || response?.data?.item || response || {});
        await loadFamilyListItems(listId);
        return updatedItem.id ? updatedItem : null;
      } catch (error) {
        setFamilyListsStatus({
          type: 'error',
          message: error?.error || error?.message || 'Could not update family list item.',
        });
        return null;
      } finally {
        setIsLoadingFamilyLists(false);
      }
    },
    [currentUser?.userId, familyClient, loadFamilyListItems]
  );

  return {
    family,
    familyLists,
    selectedFamilyList,
    selectedFamilyListItems,
    familyListsStatus,
    isLoadingFamilyLists,
    hasLoadedFamilyLists,
    openFamilyList,
    refreshFamilyLists,
    createFamilyList,
    updateFamilyList,
    createFamilyListItem,
    updateFamilyListItem,
  };
}
